import { MaterialCommunityIcons, FontAwesome6 } from '@expo/vector-icons';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Modal, Portal } from 'react-native-paper';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import DateTimePicker from 'react-native-ui-datepicker';

import { useGetTransactionHistoryPdf, useGetTransactionHistoryCsv } from '@/api/hooks/paymentAccountsHooks';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';
import BrandedButton from './BrandedButton';
import CloseButton from './CloseButton';

interface DownloadTransactionsButtonProps {
  tintColor?: string;
}

type DownloadStep = 'SELECT_FORMAT' | 'CSV_DISCLAIMER' | 'IDLE';

const DownloadTransactionsButton = ({ tintColor }: DownloadTransactionsButtonProps) => {
  const colors = useColor();
  const currentYear = new Date().getFullYear();
  const startYear = 2025;

  const [showMenu, setShowMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [step, setStep] = useState<DownloadStep>('IDLE');
  const [pendingParams, setPendingParams] = useState<{ year?: number; dateFrom?: string; dateUntil?: string } | null>(
    null
  );

  const [range, setRange] = useState<{ startDate: any; endDate: any }>({
    startDate: undefined,
    endDate: undefined
  });

  const showMenuVal = useSharedValue(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const activeTintColor = tintColor || colors.primary;

  const completedYears = useMemo(() => {
    const years: number[] = [];
    for (let y = startYear; y < currentYear; y++) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const { mutateAsync: getPdf, isPending: isPdfPending } = useGetTransactionHistoryPdf();
  const { mutateAsync: getCsv, isPending: isCsvPending } = useGetTransactionHistoryCsv();

  const handleOutsideClick = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      toggleMenu(false);
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, []);

  const toggleMenu = (show: boolean) => {
    setShowMenu(show);
    showMenuVal.value = withTiming(show ? 1 : 0, {
      duration: 300,
      easing: Easing.inOut(Easing.poly(3))
    });
  };

  const handleDownloadPdf = async (params: { year?: number; dateFrom?: string; dateUntil?: string }) => {
    try {
      const { year, dateFrom, dateUntil } = params;
      const blob = await getPdf({ year, dateFrom, dateUntil });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;

      let suffix = 'all-time';
      if (year) suffix = year.toString();
      else if (dateFrom && dateUntil) suffix = `${dateFrom}_to_${dateUntil}`;

      link.setAttribute('download', `transactions-${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setStep('IDLE');
    } catch (error) {
      console.error('Error downloading transaction history PDF:', error);
    }
  };

  const handleDownloadCsv = async (params: { year?: number; dateFrom?: string; dateUntil?: string }) => {
    try {
      await getCsv(params);
      setStep('IDLE');
      setPendingParams(null);
    } catch (error) {
      console.error('Error requesting transaction history CSV:', error);
    }
  };

  const onSelectFilter = (params: { year?: number }) => {
    setPendingParams(params);
    toggleMenu(false);
    setStep('SELECT_FORMAT');
  };

  const onShowDatePicker = () => {
    toggleMenu(false);
    setShowDatePicker(true);
  };

  const onApplyCustomRange = () => {
    if (range.startDate && range.endDate) {
      const dateFrom = format(new Date(range.startDate), 'yyyy-MM-dd');
      const dateUntil = format(new Date(range.endDate), 'yyyy-MM-dd');
      setPendingParams({ dateFrom, dateUntil });
      setShowDatePicker(false);
      setStep('SELECT_FORMAT');
    }
  };

  const onSelectPdf = () => {
    if (pendingParams) {
      handleDownloadPdf(pendingParams);
    }
  };

  const onSelectCsv = () => {
    setStep('CSV_DISCLAIMER');
  };

  const onConfirmCsv = () => {
    if (pendingParams) {
      handleDownloadCsv(pendingParams);
    }
  };

  const animatedMenuStyle = useAnimatedStyle(() => ({
    opacity: showMenuVal.value,
    transform: [
      {
        translateY: withTiming(showMenuVal.value ? 0 : -10, {
          duration: 300,
          easing: Easing.out(Easing.poly(3))
        })
      }
    ]
  }));

  const isPending = isPdfPending || isCsvPending;

  if (Platform.OS !== 'web') return null;

  return (
    <>
      <Portal>
        {/* Date Picker Modal */}
        <Modal
          visible={showDatePicker}
          onDismiss={() => setShowDatePicker(false)}
          contentContainerStyle={[styles.modal, { backgroundColor: colors.background }]}>
          <View style={styles.modalContent}>
            <CloseButton style={styles.closeButton} onCloseTap={() => setShowDatePicker(false)} />

            <Text fontWeight="900" style={[styles.modalHeader, { color: colors.titleText }]}>
              {i18n.t('mix.select_date')}
            </Text>

            <View style={styles.pickerContainer}>
              <DateTimePicker
                mode="range"
                startDate={range.startDate}
                endDate={range.endDate}
                onChange={(params) => setRange(params)}
                selectedItemColor={colors.primary}
                selectedRangeBackgroundColor={colors.progressUncompleted}
              />
            </View>

            <BrandedButton onPress={onApplyCustomRange} disabled={!range.startDate || !range.endDate}>
              {i18n.t('mix.apply')}
            </BrandedButton>
          </View>
        </Modal>

        {/* Step 1: Choose Format */}
        <Modal
          visible={step === 'SELECT_FORMAT'}
          onDismiss={() => setStep('IDLE')}
          contentContainerStyle={[styles.modal, { backgroundColor: colors.background }]}>
          <View style={styles.modalContent}>
            <CloseButton style={styles.closeButton} onCloseTap={() => setStep('IDLE')} />

            <Text fontWeight="900" style={[styles.modalHeader, { color: colors.titleText }]}>
              {i18n.t('mix.download_history')}
            </Text>

            <View style={styles.optionsRow}>
              <TouchableOpacity style={styles.optionButton} onPress={onSelectPdf}>
                <FontAwesome6 name="file-pdf" size={48} color={colors.primary} />
                <Text style={[styles.optionLabel, { color: colors.text }]}>{i18n.t('mix.download_pdf')}</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.optionButton} onPress={onSelectCsv}>
                <FontAwesome6 name="file-csv" size={48} color={colors.primary} />
                <Text style={[styles.optionLabel, { color: colors.text }]}>{i18n.t('mix.download_csv')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        {/* Step 2: CSV Disclaimer */}
        <Modal
          visible={step === 'CSV_DISCLAIMER'}
          onDismiss={() => setStep('SELECT_FORMAT')}
          contentContainerStyle={[styles.modal, { backgroundColor: colors.background }]}>
          <View style={styles.modalContent}>
            <CloseButton style={styles.closeButton} onCloseTap={() => setStep('SELECT_FORMAT')} />

            <Text fontWeight="900" style={[styles.modalHeader, { color: colors.titleText }]}>
              {i18n.t('mix.download_csv')}
            </Text>

            <Text style={[styles.disclaimerText, { color: colors.text }]}>{i18n.t('mix.csv_email_disclaimer')}</Text>

            <BrandedButton onPress={onConfirmCsv} loading={isCsvPending} disabled={isCsvPending}>
              {i18n.t('mix.confirm')}
            </BrandedButton>
          </View>
        </Modal>
      </Portal>

      <div ref={containerRef} style={{ position: 'relative', zIndex: 3000 }}>
        <TouchableOpacity
          onPress={() => toggleMenu(!showMenu)}
          style={styles.triggerContainer}
          disabled={isPending}
          activeOpacity={0.7}>
          {isPending ? (
            <ActivityIndicator size="small" color={activeTintColor} />
          ) : (
            <MaterialCommunityIcons name="download-circle-outline" size={28} color={activeTintColor} />
          )}
        </TouchableOpacity>

        {showMenu && (
          <Animated.View
            style={[
              styles.dropdown,
              { backgroundColor: colors.background, borderColor: colors.progressUncompleted },
              animatedMenuStyle
            ]}>
            <Pressable
              onPress={onShowDatePicker}
              style={({ pressed }) => [styles.dropdownItem, pressed && styles.dropdownItemPressed]}>
              <Text style={[styles.dropdownText, { color: colors.primary, fontWeight: '700' }]}>
                {i18n.t('mix.custom')}
              </Text>
            </Pressable>

            {completedYears.map((year) => (
              <Pressable
                key={year}
                onPress={() => onSelectFilter({ year })}
                style={({ pressed }) => [styles.dropdownItem, pressed && styles.dropdownItemPressed]}>
                <Text style={[styles.dropdownText, { color: colors.titleText }]}>{year}</Text>
              </Pressable>
            ))}

            <Pressable
              onPress={() => onSelectFilter({})}
              style={({ pressed }) => [styles.dropdownItem, pressed && styles.dropdownItemPressed]}>
              <Text style={[styles.dropdownText, { color: colors.titleText }]}>{i18n.t('mix.all_time')}</Text>
            </Pressable>
          </Animated.View>
        )}
      </div>
    </>
  );
};

const Text = ({ children, style, fontWeight }: any) => (
  <span
    style={{
      fontSize: 14,
      fontFamily: 'inherit',
      fontWeight: fontWeight || 'inherit',
      ...StyleSheet.flatten(style)
    }}>
    {children}
  </span>
);

const styles = StyleSheet.create({
  triggerContainer: {
    paddingRight: 15
  },

  dropdown: {
    position: 'absolute',
    top: 40,
    right: 15,
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 5,
    minWidth: 120,
    zIndex: 5000,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4
    },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 8
  },

  dropdownItem: {
    paddingHorizontal: 15,
    paddingVertical: 10,
    width: '100%',
    cursor: 'pointer'
  },

  dropdownItemPressed: {
    opacity: 0.7
  },

  dropdownText: {
    fontWeight: '500'
  },

  modal: {
    padding: 20,
    marginVertical: 20,
    width: '90%',
    maxWidth: 500,
    alignSelf: 'center',
    borderRadius: 12
  },

  modalContent: {
    flexDirection: 'column'
  },

  modalHeader: {
    fontSize: 22,
    textAlign: 'center',
    marginBottom: 20
  },

  closeButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 1
  },

  pickerContainer: {
    marginVertical: 10
  },

  optionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginVertical: 20
  },

  optionButton: {
    alignItems: 'center',
    padding: 30,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    width: '45%',
    cursor: 'pointer'
  },

  optionLabel: {
    marginTop: 15,
    fontWeight: '700',
    fontSize: 16
  },

  disclaimerText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 1.5
  }
});

export default DownloadTransactionsButton;
